import {
  type ApolloError,
  type ApolloQueryResult,
  type LazyQueryExecFunction,
  type OperationVariables,
  gql,
  useLazyQuery,
} from '@apollo/client'
import { useQuery } from '@apollo/client/react/hooks/useQuery'

import { DEFAULT_ITERATION } from '@/constants/DEFAULT_VALUES.ts'
import { useProjectId } from '@/hooks/useProjectId.ts'
import {
  INDEPENDENT_CHILDREN_GQL_SCHEMA,
  ITERATION_GQL_SCHEMA,
  LABEL_GQL_SCHEMA,
  MEMBER_GQL_SCHEMA,
  PROJECT_BASE_GQL_SCHEMA,
  PROJECT_CURRENT_ITERATION_GQL_SCHEMA,
  PROJECT_DEADLINES_GQL_SCHEMA,
  PROJECT_GQL_SCHEMA,
  PROJECT_SETTINGS_GQL_SCHEMA,
  STORY_BASE_GQL_SCHEMA,
  STORY_HISTORY_GQL_SCHEMA,
} from '@/requests/apollo/schema.ts'

export const useQueryProject = (): {
  load: LazyQueryExecFunction<Project, OperationVariables>
  loading: boolean
  error: ApolloError | undefined
  project?: Project
  refetch: (includePast: boolean) => Promise<ApolloQueryResult<Project>>
} => {
  const projectId = useProjectId()
  const [load, { loading, error, data, refetch }] = useLazyQuery(
    gql`query GetProject($projectId: ID!, $includePast: Boolean) {
      project(projectId: $projectId, includePast: $includePast) {
        ${PROJECT_GQL_SCHEMA}
      }
      members(projectId: $projectId) {
        ${MEMBER_GQL_SCHEMA}
      }
    }`,
    {
      variables: { projectId, includePast: false },
      fetchPolicy: 'network-only',
      refetchWritePolicy: 'merge',
      onError: (error) => {
        console.error('Project query error:', error)
      },
      errorPolicy: 'ignore',
    },
  )

  return {
    load,
    loading,
    error,
    project: data?.project,
    refetch: (includePast: boolean) => refetch({ projectId, includePast }),
  }
}

export const useQueryProjectMeta = (
  isEnforced = true,
): { projectMeta: ProjectMeta & ProjectSettings; loading: boolean } => {
  const projectId = useProjectId(isEnforced)
  const { data, loading } = useQuery(
    gql`query GetProjectMeta($projectId: ID!) {
      project(projectId: $projectId) {
        ${PROJECT_BASE_GQL_SCHEMA}
        ${PROJECT_SETTINGS_GQL_SCHEMA}
      }
    }`,
    {
      variables: { projectId },
      skip: !projectId,
    },
  )

  return { projectMeta: data?.project, loading }
}

export const useQueryIterations = (): {
  iterations: Iteration[]
  loading: boolean
  error: Error | undefined
} => {
  const projectId = useProjectId()
  const { data, loading, error } = useQuery(
    gql`query GetProjectIterations($projectId: ID!) {
      project(projectId: $projectId) {
        iterations {
          ${ITERATION_GQL_SCHEMA}
        }
      }
    }`,
    {
      variables: { projectId },
      skip: !projectId,
      fetchPolicy: 'cache-only',
    },
  )

  return {
    iterations: data?.project?.iterations,
    loading,
    error,
  }
}

export const useQueryStories = (storyListName: 'done' | 'unscheduled' | 'relevant'): { stories: Story[] } => {
  const projectId = useProjectId()
  const query = `query Get${storyListName.charAt(0).toUpperCase() + storyListName.slice(1)}Stories ($projectId: ID!) {
    project(projectId: $projectId) {
      ${storyListName} @client {
        ${STORY_BASE_GQL_SCHEMA}
        ${INDEPENDENT_CHILDREN_GQL_SCHEMA}
      }
    }
  }`
  const { data } = useQuery(gql`${query}`, {
    variables: { projectId },
    fetchPolicy: 'cache-only',
  })

  return { stories: data?.project?.[storyListName] ?? [] }
}

export const useQueryCurrentIteration = (): { currentIteration: Iteration } => {
  // biome-ignore lint/style/noNonNullAssertion:
  const projectId = useProjectId()!
  const { data } = useQuery(
    gql`query GetProjectCurrentIteration($projectId: ID!) {
      project(projectId: $projectId) {
        ${PROJECT_CURRENT_ITERATION_GQL_SCHEMA}
      }
    }`,
    {
      variables: { projectId },
      fetchPolicy: 'cache-only',
    },
  )

  return { currentIteration: data?.project?.currentIteration ?? DEFAULT_ITERATION(projectId) }
}

export const useQueryLabels = (): { labels: Label[] } => {
  const projectId = useProjectId()
  const { data } = useQuery(
    gql`query GetProjectCurrentIteration($projectId: ID!) {
      project(projectId: $projectId) {
        labels {
          ${LABEL_GQL_SCHEMA}
        }
      }
    }`,
    {
      variables: { projectId },
      fetchPolicy: 'cache-only',
    },
  )

  return { labels: data?.project?.labels ?? [] }
}

export const useQueryDeadlines = (): { iterationDeadlines: IterationDeadlines[] } => {
  const projectId = useProjectId()
  const { data } = useQuery(
    gql`query GetProjectDeadlines($projectId: ID!) {
      project(projectId: $projectId) {
        ${PROJECT_DEADLINES_GQL_SCHEMA}
      }
    }`,
    {
      variables: { projectId },
      fetchPolicy: 'cache-only',
    },
  )

  return { iterationDeadlines: data?.project?.iterationDeadlines ?? [] }
}

export const useQueryProjectMembers = (): { members: Member[]; refetch: () => void } => {
  const projectId = useProjectId()
  const { data, refetch } = useQuery(
    gql`query GetProjectMembers($projectId: ID!) {
      members(projectId: $projectId) {
        ${MEMBER_GQL_SCHEMA}
      }
    }`,
    {
      variables: { projectId },
      fetchPolicy: 'cache-only',
    },
  )

  return { members: data?.members ?? [], refetch }
}

export const useQueryStoryHistory = (storyId: string): { history: ProjectActivity[]; loading: boolean } => {
  const projectId = useProjectId()
  const { data, loading } = useQuery(
    gql`query GetStoryHistory($storyId: ID!) {
      storyHistory(storyId: $storyId) {
        ${STORY_HISTORY_GQL_SCHEMA}
      }
    }`,
    {
      variables: { storyId },
      skip: !projectId || !storyId,
      fetchPolicy: 'network-only',
    },
  )

  return { history: data?.storyHistory ?? [], loading }
}
